import { Injectable } from '@angular/core';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import {
    compactNavigation,
    defaultNavigation,
    ExtendedFuseNavigationItem,
    futuristicNavigation,
    horizontalNavigation,
} from 'app/mock-api/common/navigation/data';
import { NavigationFilterService } from 'app/services/navigation-filter.service';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private readonly _compactNavigation: ExtendedFuseNavigationItem[] =
        compactNavigation;
    private readonly _defaultNavigation: ExtendedFuseNavigationItem[] =
        defaultNavigation;
    private readonly _futuristicNavigation: ExtendedFuseNavigationItem[] =
        futuristicNavigation;
    private readonly _horizontalNavigation: ExtendedFuseNavigationItem[] =
        horizontalNavigation;

    /**
     * Constructor
     */
    constructor(
        private _fuseMockApiService: FuseMockApiService,
        private _navigationFilterService: NavigationFilterService
    ) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
            // Clone the original navigation data
            const compactNav = cloneDeep(this._compactNavigation);
            const defaultNav = cloneDeep(this._defaultNavigation);
            const futuristicNav = cloneDeep(this._futuristicNavigation);
            const horizontalNav = cloneDeep(this._horizontalNavigation);

            // Fill compact navigation children using the default navigation
            compactNav.forEach((compactNavItem) => {
                defaultNav.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === compactNavItem.id) {
                        compactNavItem.children = cloneDeep(
                            defaultNavItem.children
                        );
                    }
                });
            });

            // Fill futuristic navigation children using the default navigation
            futuristicNav.forEach((futuristicNavItem) => {
                defaultNav.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === futuristicNavItem.id) {
                        futuristicNavItem.children = cloneDeep(
                            defaultNavItem.children
                        );
                    }
                });
            });

            // Fill horizontal navigation children using the default navigation
            horizontalNav.forEach((horizontalNavItem) => {
                defaultNav.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === horizontalNavItem.id) {
                        horizontalNavItem.children = cloneDeep(
                            defaultNavItem.children
                        );
                    }
                });
            });

            // Apply permission filtering to all navigation types
            const filteredCompact =
                this._navigationFilterService.filterNavigationByPermissions(
                    compactNav
                );
            const filteredDefault =
                this._navigationFilterService.filterNavigationByPermissions(
                    defaultNav
                );
            const filteredFuturistic =
                this._navigationFilterService.filterNavigationByPermissions(
                    futuristicNav
                );
            const filteredHorizontal =
                this._navigationFilterService.filterNavigationByPermissions(
                    horizontalNav
                );

            // Debug logging (can be removed in production)
            console.log('=== Navigation Permission Filtering ===');
            console.log(
                'Original default navigation items:',
                defaultNav.length
            );
            console.log(
                'Filtered default navigation items:',
                filteredDefault.length
            );
            this._navigationFilterService.debugNavigationFiltering(defaultNav);

            // Return the filtered response
            return [
                200,
                {
                    compact: filteredCompact,
                    default: filteredDefault,
                    futuristic: filteredFuturistic,
                    horizontal: filteredHorizontal,
                },
            ];
        });
    }
}
